import { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLinkProps, useLocation } from 'react-router-dom';
import { Collapse, Divider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import classNames from 'classnames';

import { ButtonProps, ContentTypeIconMap, IconButton, IconOption } from '@components/base';
import { NewTag } from '@components/content';
import env from '@config/runtimeConfig';
import { IndexName } from '@enums/IndexName';
import useToggleTheme from '@hooks/useToggleTheme';
import {
  getBundlesPath,
  getChannelsPath,
  getClientConfigPath,
  getCollectionListPath,
  getContentPackagesPath,
  getContentStatisticsPath,
  getDevChunkTestPath,
  getDevComponentsPath,
  getDevicesPath,
  getDuplicateResolutionsPath,
  getEpgPath,
  getFastTrackPath,
  getFavoritesInsightsPath,
  getFocStatisticsPath,
  getFocSubscribersPath,
  getImagesPagePath,
  getIngestDiffPath,
  getIngestSearchPath,
  getIngestSettingsPath,
  getIngestSqlTitlesPath,
  getIngestSummariesPath,
  getIngestValidationsPath,
  getLayoutLandingPagePath,
  getMenuListPath,
  getMetadataValidationPath,
  getPageListPath,
  getProvidersPath,
  getQueryLogsPath,
  getSearchPath,
  getSettingsPagePath,
  getSimPath,
  getTierPackagesPath,
  getTitlePopularityPath,
  getWorkflowsPath,
} from 'src/appRoutes';

import About from './About';
import EnvSelector from './EnvSelector';
import {
  ActionButton,
  DrawerHeader,
  Nav,
  RootButton,
  RootButtonText,
  RootGroupWrapper,
  RootLink,
  StyledDrawer,
  SublistHeading,
  SublistItem,
} from './helperComponents';

type TNavMenuItemBase = {
  displayName: string;
  icon?: IconOption;
  isNew?: boolean;
};

type TNavMenuSublist = Partial<TNavMenuItemBase> & {
  children: (TNavMenuItemBase & NavLinkProps)[];
};

type TNavMenuLink = TNavMenuItemBase &
  (NavLinkProps & {
    type: 'link';
  });

type TNavMenuList = Omit<TNavMenuItemBase, 'icon'> & {
  defaultExpanded?: boolean;
  sublists: TNavMenuSublist[];
  type: 'list';
};

type TNavMenuItem = TNavMenuLink | TNavMenuList;

const NavGroup: FC<TNavMenuList> = ({ sublists, defaultExpanded, displayName }) => {
  const { pathname } = useLocation();

  const hasActiveContent = sublists?.some(sublist =>
    sublist.children.some(child => child.to && pathname.startsWith(child.to.toString()))
  );

  const [expanded, setExpanded] = useState(hasActiveContent || defaultExpanded);

  const toggle = () => setExpanded(prev => !prev);

  return (
    <RootGroupWrapper className={classNames({ expanded })}>
      <RootButton
        className={classNames({ expanded, active: hasActiveContent && !expanded })}
        leftIcon='chevronRight'
        onClick={toggle}
      >
        <RootButtonText>{displayName}</RootButtonText>
      </RootButton>

      <Collapse in={expanded}>
        <ul>
          {sublists.map(sublist => (
            <Fragment key={JSON.stringify(sublist)}>
              {sublist.displayName ? (
                <SublistHeading>
                  <Typography variant='overline'>{sublist.displayName}</Typography>
                </SublistHeading>
              ) : null}

              {sublist.children.map(({ displayName, icon, isNew, type: _type, ...rest }) => (
                <li key={displayName}>
                  <SublistItem leftIcon={icon} {...(rest as ButtonProps)}>
                    <Typography
                      className='btn-label'
                      color='text.secondary'
                      component='span'
                      flex={1}
                      variant='h6'
                    >
                      {displayName}
                    </Typography>

                    {isNew && <NewTag size='tiny' />}
                  </SublistItem>
                </li>
              ))}
            </Fragment>
          ))}
        </ul>
      </Collapse>
    </RootGroupWrapper>
  );
};

const NavMenu: FC = () => {
  const { t } = useTranslation();

  const navMenuItems: TNavMenuItem[] = [
    {
      displayName: t('landingPage.pageTitle'),
      end: true,
      icon: 'home',
      to: '/',
      type: 'link',
    },
    {
      displayName: t('navMenu.titleSearch'),
      icon: 'search',
      to: getSearchPath(IndexName.Assets),
      type: 'link',
    },
    {
      type: 'list',
      defaultExpanded: true,
      displayName: t('navMenu.layouts.main'),
      sublists: [
        {
          children: [
            {
              displayName: t('overview'),
              end: true,
              icon: 'overview',
              to: getLayoutLandingPagePath(),
              type: 'link',
            },
            {
              displayName: t('navMenu.layouts.settings'),
              icon: 'settings',
              to: getSettingsPagePath(),
              type: 'link',
            },
          ],
        },
        {
          displayName: 'Structure',
          children: [
            {
              displayName: t('navMenu.layouts.rootMenus'),
              icon: 'rootMenu',
              to: getMenuListPath(true),
              type: 'link',
            },
            {
              displayName: t('navMenu.layouts.pages'),
              icon: ContentTypeIconMap.page,
              to: getPageListPath({ types: ['swimlane'] }),
              type: 'link',
            },
          ],
        },
        {
          displayName: 'Content',
          children: [
            {
              displayName: t('navMenu.layouts.collections'),
              icon: ContentTypeIconMap.collection,
              to: getCollectionListPath(),
              type: 'link',
            },
            {
              displayName: t('navMenu.layouts.menus'),
              icon: ContentTypeIconMap.menu,
              to: getMenuListPath(),
              type: 'link',
            },
            {
              displayName: t('navMenu.layouts.images'),
              icon: 'images',
              to: getImagesPagePath(),
              type: 'link',
            },
          ],
        },
      ],
    },
    {
      displayName: t('statistics'),
      type: 'list',
      sublists: [
        {
          children: [
            {
              displayName: t('contentStatistics'),
              icon: 'chart',
              end: false,
              to: getContentStatisticsPath(),
              type: 'link',
            },
            {
              displayName: t('queryLogs'),
              icon: 'article',
              to: getQueryLogsPath(),
              type: 'link',
            },
            {
              displayName: t('titlePopularity'),
              icon: 'insights',
              to: getTitlePopularityPath(),
              type: 'link',
            },
            {
              displayName: t('favoritesInsights'),
              icon: 'insights',
              to: getFavoritesInsightsPath(),
              type: 'link',
            },
            {
              displayName: t('foc.stats.title'),
              icon: 'chart',
              to: getFocStatisticsPath(),
              type: 'link',
            },
          ],
        },
      ],
    },
    {
      displayName: t('navMenu.operations.main'),
      type: 'list',
      sublists: [
        {
          children: [
            {
              displayName: t('navMenu.operations.workflows'),
              icon: 'schema',
              to: getWorkflowsPath(),
              type: 'link',
            },
            {
              displayName: t('navMenu.operations.metadataValidation'),
              icon: 'rule',
              to: getMetadataValidationPath(),
              type: 'link',
            },
            {
              displayName: t('duplicateResolution'),
              icon: 'nearbyError',
              to: getDuplicateResolutionsPath(),
              type: 'link',
            },
            {
              displayName: t('epgMonitoring'),
              icon: 'epg',
              to: getEpgPath(),
              type: 'link',
            },
            {
              displayName: t('simulationTools'),
              icon: 'atom',
              to: getSimPath(),
              type: 'link',
            },
            {
              displayName: t('clientConfig'),
              icon: 'construction',
              to: getClientConfigPath(),
              type: 'link',
            },
            {
              displayName: t('foc.fastTrack'),
              icon: 'cloudSync',
              isNew: true,
              to: getFastTrackPath(),
              type: 'link',
            },
            {
              displayName: t('navMenu.operations.devices'),
              icon: 'devices',
              isNew: true,
              to: getDevicesPath(),
              type: 'link',
            },
            {
              displayName: t('foc.ops.title'),
              icon: 'cloudSync',
              isNew: true,
              to: getFocSubscribersPath(),
              type: 'link',
            },
          ],
        },
      ],
    },

    {
      displayName: t('navMenu.content'),
      type: 'list',
      sublists: [
        {
          children: [
            {
              displayName: t('providers'),
              icon: 'provider',
              to: getProvidersPath(),
              type: 'link',
            },
            {
              displayName: t('foc.bundles'),
              icon: 'folder',
              isNew: true,
              to: getBundlesPath(),
              type: 'link',
            },
            {
              displayName: t('foc.packages'),
              icon: 'contentPackage',
              isNew: true,
              to: getContentPackagesPath(),
              type: 'link',
            },
            {
              displayName: 'Tiered packages',
              icon: 'contentPackage',
              isNew: true,
              to: getTierPackagesPath(),
              type: 'link',
            },
            {
              displayName: t('channels'),
              icon: 'tv',
              isNew: true,
              to: getChannelsPath(),
              type: 'link',
            },
          ],
        },
      ],
    },

    ...(env !== 'prod'
      ? [
          {
            displayName: 'Dev Tools',
            type: 'list',
            sublists: [
              {
                children: [
                  {
                    displayName: 'Components',
                    icon: 'components',
                    to: getDevComponentsPath(),
                    type: 'link',
                  },
                  {
                    displayName: 'Load Chunk Test',
                    icon: 'cloudSync',
                    to: getDevChunkTestPath(),
                    type: 'link',
                  },
                ],
              },
            ],
          } as TNavMenuItem,
        ]
      : []),
    {
      displayName: 'New Ingest',
      type: 'list',
      sublists: [
        {
          children: [
            {
              displayName: 'AI search',
              icon: 'search',
              to: getIngestSearchPath(IndexName.Assets),
              type: 'link',
            },
            {
              displayName: 'AI search diff',
              icon: 'compareArrows',
              to: getIngestDiffPath(),
              type: 'link',
            },
            {
              displayName: 'SQL titles',
              icon: 'codeBracesBox',
              to: getIngestSqlTitlesPath(),
              type: 'link',
            },
            {
              displayName: 'Summaries',
              icon: 'article',
              to: getIngestSummariesPath(),
              type: 'link',
            },
            {
              displayName: 'Validations',
              icon: 'rule',
              to: getIngestValidationsPath(),
              type: 'link',
            },
            {
              displayName: 'Settings',
              icon: 'settings',
              to: getIngestSettingsPath(),
              type: 'link',
            },
          ],
        },
      ],
    },
  ];

  return (
    <Nav>
      <Stack component='ul' spacing={0.5}>
        {navMenuItems.map(navMenuItem => {
          const { type, displayName, ...rest } = navMenuItem;

          return type === 'list' ? (
            <NavGroup {...navMenuItem} key={displayName} />
          ) : type === 'link' ? (
            <li key={displayName}>
              <RootLink {...(rest as ButtonProps)} key={displayName} leftIcon={navMenuItem.icon}>
                <RootButtonText>{displayName}</RootButtonText>
              </RootLink>
            </li>
          ) : null;
        })}
      </Stack>
    </Nav>
  );
};

type AppMenuProps =
  | {
      onClose: VoidFunction;
      open: boolean;
      xsScreen: true;
    }
  | {
      onClose?: undefined;
      open: true;
      xsScreen?: false;
    };

const AppMenu: FC<AppMenuProps> = ({ open, onClose, xsScreen }) => {
  const { t } = useTranslation();

  const {
    breakpoints,
    palette: { mode },
  } = useTheme();

  const toggleTheme = useToggleTheme();

  const isSmallScreen = useMediaQuery(breakpoints.down('sm'));

  if (!xsScreen && isSmallScreen) return null;

  return (
    <StyledDrawer
      onClose={onClose}
      open={open}
      slotProps={{
        paper: {
          sx: {
            background: theme => theme.palette.background.default,
            borderBottom: 'none',
            borderLeft: 'none',
            borderTop: 'none',
            overflowY: 'overlay',
          },
        },
      }}
      variant={xsScreen ? 'temporary' : 'permanent'}
    >
      <div>
        <DrawerHeader>
          {xsScreen && <IconButton icon='chevronLeft' onClick={onClose} title={t('close')} />}
        </DrawerHeader>

        <Divider />

        <NavMenu />
      </div>

      <Stack alignItems='center' p={1}>
        <About />

        <ActionButton leftIcon={mode === 'dark' ? 'lightMode' : 'darkMode'} onClick={toggleTheme}>
          {t('theme.switch', { context: mode })}
        </ActionButton>

        <EnvSelector />
      </Stack>
    </StyledDrawer>
  );
};

export default AppMenu;
